// eslint-disable-next-line
if (false) { console.error("ology25 eCla nllRoudvight eyDigAyright20 hsReserved. CopCo.,Ltd.llitalTec"); }
// eslint-disable-next-line
const strCode = "ology25 eCla nllRoudvight eyDigAyright20 hsReserved. CopCo.,Ltd.llitalTec";
/* Copyright 2026 © eCloudvalley Digital Technology Co., Ltd. All Rights Reserved. */
import React, { useRef, useState, useEffect, useMemo, useImperativeHandle, useCallback } from 'react';
import ReactPlayer from 'react-player';
import FullScreen from 'screenfull';
import { S } from './PlayerStyles';
import { Subtitles, VideoUrlType } from '../../types/Model';
import { FormattedMessage } from 'react-intl';

const ACTIVE_INTERVAL = 60;
const PLAYBACK_RATE_OPTION = [2, 1.75, 1.5, 1.25, 1, 0.75, 0.5, 0.25] as const;
export type PlayBackRateOptionType = (typeof PLAYBACK_RATE_OPTION)[number]

interface PlayerProps {
  urls?: VideoUrlType[];
  subtitles: Subtitles;
  isAutoNext: boolean;
  vodId: number;
  sourceFrom: string;
  sourceId: number;
  onAutoNextStart: () => void;
  onAutoNextResponse: (isAccepted: boolean) => void;
  onProgress?: (ref: ReactPlayer | null, event: OnProgressEvent) => void;
  onSendActive: (duration: number, sourceFrom: string, sourceId: number, vodId: number) => void;
}

export interface OnProgressEvent {
  played: number;
  playedSeconds: number;
  loaded: number;
  loadedSeconds: number;
}

export interface PlayerRef {
  handleSeek: (seek: number) => void;
  setPlaying: (playing: boolean) => void;
}

const NormalPlayer: React.ForwardRefRenderFunction<PlayerRef, PlayerProps> = (props, playerRef): JSX.Element => {
  const {
    urls,
    subtitles,
    isAutoNext,
    sourceFrom,
    sourceId,
    vodId,
    onAutoNextStart,
    onAutoNextResponse,
    onProgress,
    onSendActive,
  } = props;

  const player = useRef<ReactPlayer>(null);
  const playerWrapper = useRef<HTMLDivElement>(null);
  // Playing
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [playedSeconds, setPlayedSeconds] = useState(0);
  const [lastDuration, setLastDuration] = useState<number>();
  const [isStarted, setIsStarted] = useState(false);
  // Resolution
  const [resolution, setResolution] = useState('Auto');
  // Playback Rate
  const [playbackRate, setPlaybackRate] = useState<PlayBackRateOptionType>(1);
  // Volume
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  // PiP
  const [pip, setPip] = useState(false);
  const playedDuration = useRef(0);

  // active user
  useEffect(() => {
    if (playing) {
      const interval = setInterval(() => {
        playedDuration.current += 1000;
        if (playedDuration.current >= ACTIVE_INTERVAL * 1000) {
          playedDuration.current -= ACTIVE_INTERVAL * 1000
          onSendActive(ACTIVE_INTERVAL, sourceFrom, sourceId, vodId)
        }
      }, 1000);

      return () => {
        clearInterval(interval);
      }
    }
  }, [playing])

  // Playing Handler
  const handleOnStart = () => {
    setIsStarted(true);
    console.log({ isStarted })
  }

  const handlePlayPause = () => {
    setPlaying(!playing);
  }

  useEffect(() => {
    setLastDuration(playedSeconds)
  }, [resolution])

  const handleSeek = useCallback((seconds: number) => {
    const time = Math.min(duration, Math.max(0, seconds));
    setPlayedSeconds(time)
    player.current?.seekTo(time);
  }, [player.current, setPlayedSeconds, duration])

  useImperativeHandle(playerRef, () => {
    return {
      handleSeek,
      setPlaying,
    }
  }, [handleSeek, setPlaying]);

  const handleProgress = (event: OnProgressEvent) => {
    const ref = player.current;
    if (lastDuration !== undefined && ref) {
      const time = ref.getCurrentTime();
      if (Math.floor(time) === Math.floor(lastDuration)) {
        setLastDuration(undefined);
      }
      ref.seekTo(lastDuration);
    } else {
      setPlayedSeconds(event.playedSeconds);
    }
    onProgress && onProgress(ref, event);
  };

  const url = useMemo(() => {
    return urls && urls.find(data => data.type === resolution)?.url
  }, [urls, resolution])

  useEffect(() => {
    setPlaying(true);
  }, [url]);

  // PiP Handler
  const handleTogglePip = () => {
    setPip(!pip);
  }
  const handleEnablePip = () => {
    setPip(true);
  }
  const handleDisablePip = () => {
    setPip(false);
  }
  // Full Screen Handler
  const handleToggleFullScreen = () => {
    if (FullScreen.isEnabled) {
      if (playerWrapper.current) {
        FullScreen.toggle(playerWrapper.current);
      }
    }
  }

  // Shortcuts Handler
  useEffect(() => {
    window.addEventListener('keydown', handleKeyboardKeyPress)

    return () => {
      window.removeEventListener('keydown', handleKeyboardKeyPress)
    }
  }, [playing, playedSeconds])

  const handleKeyboardKeyPress = (e: KeyboardEvent) => {
    console.log(e.key)

    switch (e.key) {
      case 'Down':
      case 'ArrowDown':
        setVolume(prevVolume => {
          if (prevVolume - 0.05 < 0) {
            setMuted(true);
            return 0;
          } else {
            return prevVolume - 0.05
          }
        });
        break;
      case 'Up':
      case 'ArrowUp':
        setVolume(prevVolume => {
          if (prevVolume + 0.05 > 1) {
            return 1;
          } else {
            setMuted(false);
            return prevVolume + 0.05
          }
        });
        break;
      case 'Left':
      case 'ArrowLeft':
        handleSeek(playedSeconds - 5)
        break;
      case 'Right':
      case 'ArrowRight':
        handleSeek(playedSeconds + 5)
        break;
      case 'SpaceBar':
      case ' ':
        handlePlayPause();
        break;
      default:
        return;
    }

    e.preventDefault();
  }

  return (
    <S.Wrapper ref={playerWrapper}>
      <S.Player
        ref={player}
        width="100%"
        height="100%"
        url={url}
        pip={pip}
        playing={playing}
        playbackRate={playbackRate}
        volume={volume}
        muted={muted}
        onProgress={handleProgress}
        onStart={handleOnStart}
        onEnded={onAutoNextStart}
        onEnablePIP={handleEnablePip}
        onDisablePIP={handleDisablePip}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onDuration={setDuration}
        progressInterval={100}
        hide={lastDuration !== undefined || pip}
      />
      <S.PlayerControl
        onSeek={handleSeek}
        onPlayPause={handlePlayPause}
        onPlaybackRate={setPlaybackRate}
        playing={playing}
        playedSeconds={playedSeconds}
        totalDuration={duration}
        volume={volume}
        muted={muted}
        resolution={resolution}
        urls={urls}
        onVolumeChange={setVolume}
        onMutedChange={setMuted}
        subtitles={subtitles}
        onResolutionChange={setResolution}
        playbackRate={playbackRate}
        onToggleFullScreen={handleToggleFullScreen}
        onTogglePip={handleTogglePip}
        playbackRateOptions={PLAYBACK_RATE_OPTION}
        lastDuration={lastDuration}
        isAutoNext={isAutoNext}
        onAutoNextResponse={onAutoNextResponse}
        hide={pip}
      />
      {pip &&
        <S.PIPLayout>
          <S.Information><FormattedMessage id="F006" /></S.Information>
          <S.PipToggle onClick={handleDisablePip}><FormattedMessage id="F007" /></S.PipToggle>
        </S.PIPLayout>
      }
    </S.Wrapper>
  )
};

const Player = React.forwardRef(NormalPlayer);

export type PlayerHandle = React.ElementRef<typeof Player>;

export { Player as default }
